import { useState, Suspense, useEffect } from 'react';

import { WorkPlaceResponse } from 'generated';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { mutations, queries } from 'RestAPIs/rapini';

import { planUnitsAtom } from '../../atoms';

import { Box, Button, Typography } from '@mui/material';

import { DataGrid, Dialog, FontAwesomeSvgIcon } from 'components/shared';

import { useQueryClient } from 'react-query';

import AddWorkPlaceDialog from './AddWorkPlaceDialog';
import { getPlanUnitIds } from '../../../../utils';
import {
  faArrowDownArrowUp,
  faDown,
  faUp
} from '@fortawesome/pro-light-svg-icons';
import { defaultDateFormatter } from 'utils/grid';

interface WorkPlaceRow extends WorkPlaceResponse {
  entityId?: string;
}

export default function WorkPlacesDialog({ open, handleClose }) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSortMode, setIsSortMode] = useState(false);
  const [workPlaceToEdit, setWorkPlaceToEdit] = useState<
    WorkPlaceResponse | undefined
  >();
  const queryClient = useQueryClient();
  const [planunits] = useRecoilState(planUnitsAtom);

  const organizationalUnitIds = getPlanUnitIds(planunits);

  const { data: workPlaces } = queries.useGetWorkPlaces(
    undefined,
    undefined,
    //@ts-expect-error TODO: BE should accept array of ids
    organizationalUnitIds.join(','),
    {
      enabled: open && organizationalUnitIds.length > 0
    }
  );
  const [rows, setRows] = useState<WorkPlaceRow[]>([]);
  const { mutate: sortWorkPlaces } =
    mutations.useSortWorkPlaceAtOrganizationalUnitAssignment();

  const columns = [
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'validFrom',
      valueGetter: (params) => defaultDateFormatter(params),
      headerName: t('validFrom'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'validTo',
      valueGetter: (params) => defaultDateFormatter(params),
      headerName: t('validTo'),
      flex: 1,
      minWidth: 150
    }
  ];

  useEffect(() => {
    const newRows = workPlaces?.map((a) => ({ ...a, id: a.entityId })) ?? [];
    setRows(newRows);
  }, [workPlaces]);

  const firstWorkPlaceEntityId = rows?.[0]?.entityId;
  const lastWorkPlaceEntityId = rows?.[rows.length - 1]?.entityId;

  function onAddClick() {
    setWorkPlaceToEdit(undefined);
    setDialogOpen(true);
  }

  function onEditClick(id) {
    const editValues = workPlaces?.find((a) => a.entityId === id);

    setWorkPlaceToEdit(editValues);
    setDialogOpen(true);
  }

  function onCancelClick() {
    setIsSortMode(false);
  }

  function onSaveClick() {
    organizationalUnitIds.forEach((organizationalUnitId) => {
      sortWorkPlaces(
        {
          organizationalUnitId,
          sortedWorkPlaceEntityIds: rows
            .map((a) => a.entityId)
            .filter((entityId): entityId is string => !!entityId)
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries('planning');
          }
        }
      );
    });
    setIsSortMode(false);
  }

  function moveUp(id) {
    const newRows = [...rows];
    const index = newRows.findIndex((e) => e.entityId == id);
    if (index > 0) {
      const el = newRows[index];
      newRows[index] = newRows[index - 1];
      newRows[index - 1] = el;
      setRows(newRows);
    }
  }

  function moveDown(id) {
    const newRows = [...rows];
    const index = newRows.findIndex((e) => e.entityId == id);
    if (index !== -1 && index < newRows.length - 1) {
      const el = newRows[index];
      newRows[index] = newRows[index + 1];
      newRows[index + 1] = el;
      setRows(newRows);
    }
  }

  function handleAddWorkPlaceDialogClose() {
    setIsSortMode(false);
    setDialogOpen(false);
    setWorkPlaceToEdit(undefined);
  }

  return (
    <Dialog
      title={t('activityPlanning.manageWorkPlaces')}
      maxWidth="xl"
      open={open}
      handleClose={handleClose}
      onClose={handleClose}
      PaperProps={{
        sx: { width: '100%' }
      }}
    >
      <Suspense fallback={<Typography>Loading</Typography>}>
        <DataGrid
          columns={columns}
          rows={rows}
          onAddClick={onAddClick}
          onAddClickTitle="Werkplek toevoegen"
          onEditClick={!isSortMode ? onEditClick : undefined}
          addRowDialog={
            <AddWorkPlaceDialog
              open={dialogOpen}
              workPlace={workPlaceToEdit}
              handleClose={() => handleAddWorkPlaceDialogClose()}
            />
          }
          additionalButtons={
            rows?.length > 1
              ? [
                  {
                    label: t('sort'),
                    startIcon: <FontAwesomeSvgIcon icon={faArrowDownArrowUp} />,
                    onClick: () => setIsSortMode(true),
                    disabled: isSortMode,
                    color: 'dark'
                  }
                ]
              : []
          }
          customActions={(id) =>
            isSortMode
              ? [
                  ...(id !== firstWorkPlaceEntityId
                    ? [
                        {
                          icon: faUp,
                          tooltipTitle: t('up'),
                          onClick: () => moveUp(id)
                        }
                      ]
                    : []),
                  ...(id !== lastWorkPlaceEntityId
                    ? [
                        {
                          icon: faDown,
                          tooltipTitle: t('down'),
                          onClick: () => moveDown(id)
                        }
                      ]
                    : [])
                ]
              : []
          }
        />
        {isSortMode && (
          <Box sx={{ display: 'flex', width: '100%' }}>
            <Box ml="auto" mt="12px" display="flex" gap={1}>
              <Button variant="outlined" onClick={onCancelClick}>
                {t('cancel')}
              </Button>
              <Button variant="contained" color="dark" onClick={onSaveClick}>
                {t('save')}
              </Button>
            </Box>
          </Box>
        )}
      </Suspense>
    </Dialog>
  );
}
